/**
 * PublicGuidePage - Public read-only Experience Hub
 * 
 * This is a thin wrapper that uses the shared ExperienceHubCore component.
 * It fetches public data and renders the same Experience Hub as private,
 * but with readOnly=true (no edit controls).
 */

import React, { useState, useEffect, useMemo, useCallback, Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { 
  MapPin, Menu, X, Loader2, AlertCircle, Eye, ChevronRight, ChevronDown
} from 'lucide-react';
import ExperienceHubCore from '../components/ExperienceHubCore';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== ERROR BOUNDARY (Public-only) ====================
// Catches runtime errors in ExperienceHubCore and shows graceful fallback

class PublicExperienceErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('PublicExperienceErrorBoundary caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0f172a] flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <h1 className="text-white text-xl font-bold mb-2">Experience Not Available</h1>
            <p className="text-white/70 mb-6">This guide could not be displayed. Please try again later.</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ==================== HELPER FUNCTIONS ====================

const getTrafficLightLabel = (trafficLight) => {
  switch (trafficLight) {
    case 'green': return 'Low';
    case 'amber': return 'Moderate';
    case 'red': return 'High';
    default: return 'Not rated';
  }
};

const getTrafficLightColor = (trafficLight) => {
  switch (trafficLight) {
    case 'green': return '#22c55e';
    case 'amber': return '#f59e0b';
    case 'red': return '#ef4444';
    default: return '#94a3b8';
  }
};

// ==================== BREAKDOWN VIEW COMPONENT ====================
// Simple read-only breakdown view for public "View Sensory Details"

const BreakdownView = ({ area, onClose }) => {
  if (!area) return null;
  
  const categories = area.categories || [];
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#1e3a5f] rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-[#1a365d] px-4 py-3 flex items-center justify-between sticky top-0">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-white/70" />
            <span className="text-white font-semibold">Sensory Details</span>
          </div>
          <button onClick={onClose} className="p-2 text-white/70 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Area Header */}
        <div className="p-4 border-b border-white/10">
          <h2 className="text-white text-xl font-bold mb-1">{area.area_name || area.name}</h2>
          <div className="flex items-center gap-2">
            <span 
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: getTrafficLightColor(area.trafficLight) }}
            />
            <span className="text-white/80 text-sm">
              {area.label || getTrafficLightLabel(area.trafficLight)} sensory environment
            </span>
          </div>
        </div>
        
        {/* Categories */}
        <div className="p-4 space-y-3">
          {categories.length > 0 ? (
            categories.map((cat, index) => (
              <div 
                key={index}
                className="bg-white/5 rounded-xl p-3 flex items-center justify-between"
              >
                <span className="text-white text-sm">{cat.category}</span>
                <span 
                  className="px-2 py-1 rounded text-xs font-medium"
                  style={{ 
                    backgroundColor: `${getTrafficLightColor(cat.traffic_light)}20`,
                    color: getTrafficLightColor(cat.traffic_light)
                  }}
                >
                  {getTrafficLightLabel(cat.traffic_light)}
                </span>
              </div>
            ))
          ) : (
            <p className="text-white/50 text-sm text-center py-4">
              No detailed sensory data available for this area.
            </p>
          )}
        </div>
        
        {/* Description */}
        {area.description && (
          <div className="px-4 pb-4">
            <h3 className="text-white/60 text-xs uppercase tracking-wider mb-2">About this area</h3>
            <p className="text-white/80 text-sm leading-relaxed">{area.description}</p>
          </div>
        )}
        
        {/* Close button */}
        <div className="p-4 pt-0">
          <button
            onClick={onClose}
            className="w-full bg-white/10 hover:bg-white/20 text-white py-3 rounded-xl font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ==================== MENU MODAL ====================

const MenuModal = ({ isOpen, onClose, venueName }) => {
  if (!isOpen) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25 }}
        className="absolute right-0 top-0 bottom-0 w-72 bg-[#1e3a5f] p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <span className="text-white font-bold text-lg">Menu</span>
          <button onClick={onClose} className="p-2 text-white/70 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-2">
          <div className="text-white/50 text-xs uppercase tracking-wider mb-2">Venue</div>
          <div className="text-white font-medium">{venueName}</div>
        </div>
        
        <div className="mt-6 pt-6 border-t border-white/10">
          <div className="text-white/50 text-xs uppercase tracking-wider mb-2">About</div>
          <p className="text-white/70 text-sm">
            This sensory guide helps visitors prepare for their visit by understanding the sensory environment of different areas.
          </p>
        </div>
        
        <div className="mt-6 pt-6 border-t border-white/10">
          <div className="text-white/50 text-xs uppercase tracking-wider mb-2">Powered by</div>
          <span className="text-white font-bold text-lg tracking-wide">nysa</span>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ==================== MAIN COMPONENT ====================

const PublicGuidePage = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  
  // ========== DATA STATE ==========
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notAvailable, setNotAvailable] = useState(false);
  const [guide, setGuide] = useState(null);
  const [venue, setVenue] = useState(null);
  const [areas, setAreas] = useState([]);
  const [movementSteps, setMovementSteps] = useState([]);
  const [submission, setSubmission] = useState(null);
  
  // ========== UI STATE (passed to ExperienceHubCore) ==========
  const [currentScreen, setCurrentScreen] = useState('explore_hub');
  const [selectedAreaId, setSelectedAreaId] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  const [routeTargetArea, setRouteTargetArea] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showVenueAbout, setShowVenueAbout] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  
  // ========== BREAKDOWN STATE (public-specific) ==========
  const [breakdownArea, setBreakdownArea] = useState(null);
  
  // ========== DATA FETCHING ==========
  useEffect(() => {
    fetchPublicData();
  }, [submissionId]);
  
  const fetchPublicData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/public/guide/${submissionId}`);
      const data = response.data;
      
      if (data.not_available) {
        setNotAvailable(true);
        setError(data.message);
        return;
      }
      
      setGuide(data.guide);
      setVenue(data.venue);
      setAreas(data.areas || []);
      setMovementSteps(data.movement_steps || []);
      setSubmission(data.submission);
    } catch (err) {
      setError('Unable to load this experience');
      setNotAvailable(true);
    } finally {
      setLoading(false);
    }
  };
  
  // ========== DEFENSIVE NORMALIZATION (must be before any returns) ==========
  // Ensure arrays are arrays before any processing
  const safeAreas = useMemo(() => Array.isArray(areas) ? areas : [], [areas]);
  const safeMovementSteps = useMemo(() => Array.isArray(movementSteps) ? movementSteps : [], [movementSteps]);
  const safeSubmission = useMemo(() => {
    if (submission && typeof submission === 'object') {
      return {
        ...submission,
        custom_routes: Array.isArray(submission.custom_routes) ? submission.custom_routes : []
      };
    }
    return { custom_routes: [] };
  }, [submission]);
  
  // Validate payload has critical fields
  const hasValidPayload = useMemo(() => {
    if (!guide || typeof guide !== 'object') return false;
    if (!guide.sensory_summary || typeof guide.sensory_summary !== 'object') return false;
    if (!Array.isArray(guide.sensory_summary.areas)) return false;
    return true;
  }, [guide]);

  // ========== COMPUTED DATA ==========
  
  // Get combined areas with sensory data (same logic as private GuidePage)
  // Uses defensive safeAreas to prevent crashes on malformed data
  const allAreasWithData = useMemo(() => {
    const sensoryAreas = guide?.sensory_summary?.areas || [];
    if (!Array.isArray(sensoryAreas)) return [];
    
    return sensoryAreas.map(sensoryArea => {
      if (!sensoryArea || typeof sensoryArea !== 'object') return null;
      const fullData = safeAreas.find(a => a && a.area_id === sensoryArea.area_id) || {};
      const areaImage = fullData.photos?.[0]?.url || fullData.images?.[0]?.url || fullData.images?.[0] || fullData.image_url;
      const photosArray = fullData.photos || [];
      
      return {
        ...sensoryArea,
        ...fullData,
        image: areaImage,
        photos: photosArray,
        trafficLight: sensoryArea.overall_traffic_light,
        label: getTrafficLightLabel(sensoryArea.overall_traffic_light),
        overall: { traffic_light: sensoryArea.overall_traffic_light },
        categories: Array.isArray(sensoryArea.categories) ? sensoryArea.categories : []
      };
    }).filter(Boolean);
  }, [safeAreas, guide]);
  
  // Sensory summary
  const sensorySummary = guide?.sensory_summary || {};
  
  // Weekly busyness data
  const weeklyBusyness = submission?.weekly_busyness || {};
  const busynessNotes = submission?.busyness_notes || '';
  
  // Calculate real-time venue status
  const venueStatus = useMemo(() => {
    const now = new Date();
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = days[now.getDay()];
    const hour = now.getHours();
    
    let timeBlock;
    if (hour >= 6 && hour < 12) {
      timeBlock = 'morning';
    } else if (hour >= 12 && hour < 17) {
      timeBlock = 'afternoon';
    } else if (hour >= 17 && hour < 24) {
      timeBlock = 'evening';
    } else {
      timeBlock = 'morning';
    }
    
    const currentBusyness = weeklyBusyness?.[currentDay]?.[timeBlock];
    
    const STATUS_CONFIG = {
      quiet: {
        status: 'calm',
        label: 'Calm',
        color: '#22c55e',
        colorLight: '#22c55e15',
        summary: 'Generally calm with low noise and movement.',
        reassurance: 'You should feel comfortable here.',
        hasData: true
      },
      moderate: {
        status: 'moderate',
        label: 'Moderate',
        color: '#f59e0b',
        colorLight: '#f59e0b15',
        summary: 'Can feel moderately busy with some noise.',
        reassurance: 'You can handle this.',
        hasData: true
      },
      busy: {
        status: 'busy',
        label: 'Busy',
        color: '#f97316',
        colorLight: '#f9731615',
        summary: 'Expect higher activity, noise, and movement.',
        reassurance: 'Plan quiet breaks if needed.',
        hasData: true
      },
      intense: {
        status: 'intense',
        label: 'Very Busy',
        color: '#ef4444',
        colorLight: '#ef444415',
        summary: 'Peak times with high stimulation expected.',
        reassurance: 'Consider off-peak visits.',
        hasData: true
      }
    };
    
    if (currentBusyness && STATUS_CONFIG[currentBusyness]) {
      return STATUS_CONFIG[currentBusyness];
    }
    
    return {
      status: 'not_set',
      label: 'Status Unknown',
      color: '#94a3b8',
      colorLight: '#94a3b815',
      summary: 'Visitor levels have not been set for this time.',
      reassurance: null,
      hasData: false
    };
  }, [weeklyBusyness]);
  
  // Best time to visit
  const bestTimeToVisit = useMemo(() => {
    if (!weeklyBusyness || Object.keys(weeklyBusyness).length === 0) return null;
    
    const quietTimes = [];
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const timeBlocks = ['morning', 'afternoon', 'evening'];
    const timeLabels = { morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening' };
    
    days.forEach(day => {
      timeBlocks.forEach(time => {
        if (weeklyBusyness[day]?.[time] === 'quiet') {
          quietTimes.push({ day, time });
        }
      });
    });
    
    if (quietTimes.length === 0) return null;
    
    const first = quietTimes[0];
    const dayLabel = first.day.charAt(0).toUpperCase() + first.day.slice(1);
    return `${dayLabel} ${timeLabels[first.time]}`;
  }, [weeklyBusyness]);
  
  // ========== LOADING STATE ==========
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0f172a] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-white/50 animate-spin mx-auto mb-4" />
          <p className="text-white/70">Loading experience...</p>
        </div>
      </div>
    );
  }
  
  // ========== ERROR STATE ==========
  if (notAvailable || error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0f172a] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h1 className="text-white text-xl font-bold mb-2">Experience Not Available</h1>
          <p className="text-white/70 mb-6">{error || 'This guide is not available publicly yet.'}</p>
        </div>
      </div>
    );
  }
  
  // ========== PAYLOAD VALIDATION CHECK (uses hasValidPayload computed above) ==========
  if (!hasValidPayload) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0f172a] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h1 className="text-white text-xl font-bold mb-2">Experience Not Available</h1>
          <p className="text-white/70 mb-6">This guide data is incomplete or unavailable.</p>
        </div>
      </div>
    );
  }
  
  // ========== MAIN RENDER ==========
  const brandColor = venue?.brand_color_1 || '#1e3a5f';
  return (
    <div 
      className="min-h-screen relative overflow-hidden"
      style={{ 
        background: `linear-gradient(180deg, ${brandColor} 0%, #4a3f6b 25%, #7c6a8a 50%, #d4a574 75%, #e8c49a 100%)`
      }}
    >
      {/* Main Container - Same width as private GuidePage */}
      <div className="relative mx-auto min-h-screen flex flex-col max-w-7xl">
        <main className="flex-1 px-6 pt-4 pb-6">
          <PublicExperienceErrorBoundary>
            <AnimatePresence mode="wait">
              <ExperienceHubCore
                // Mode props
                readOnly={true}
                isOwner={false}
                
                // Data props (using normalized safe values)
                allAreasWithData={allAreasWithData}
                sensorySummary={sensorySummary}
                venue={venue || {}}
                venueStatus={venueStatus}
                busynessNotes={busynessNotes}
                movementSteps={safeMovementSteps}
                submission={safeSubmission}
                submissionId={submissionId}
                
                // State props
                selectedAreaId={selectedAreaId}
                setSelectedAreaId={setSelectedAreaId}
                selectedArea={selectedArea}
                setSelectedArea={setSelectedArea}
                mobileDetailOpen={mobileDetailOpen}
                setMobileDetailOpen={setMobileDetailOpen}
                currentScreen={currentScreen}
                setCurrentScreen={setCurrentScreen}
                routeTargetArea={routeTargetArea}
                setRouteTargetArea={setRouteTargetArea}
                currentStepIndex={currentStepIndex}
                setCurrentStepIndex={setCurrentStepIndex}
                showVenueAbout={showVenueAbout}
                setShowVenueAbout={setShowVenueAbout}
                menuOpen={menuOpen}
                setMenuOpen={setMenuOpen}
                
                // Journey Builder - disabled for public
                journeyEditMode={false}
                setJourneyEditMode={null}
                
                // Callbacks
                onViewSensoryDetails={(area) => setBreakdownArea(area)}
                onEditBusyness={null}
                onAddMovementStep={null}
                onEditMovementStep={null}
                onDeleteMovementStep={null}
                onShareGuide={null}
                
                // Computed data
                bestTimeToVisit={bestTimeToVisit}
                busynessLastUpdated={null}
              />
            </AnimatePresence>
          </PublicExperienceErrorBoundary>
        </main>
      </div>
      
      {/* Menu Modal */}
      <AnimatePresence>
        {menuOpen && (
          <MenuModal 
            isOpen={menuOpen} 
            onClose={() => setMenuOpen(false)} 
            venueName={venue?.name || 'Venue'}
          />
        )}
      </AnimatePresence>
      
      {/* Breakdown View Modal */}
      <AnimatePresence>
        {breakdownArea && (
          <BreakdownView 
            area={breakdownArea} 
            onClose={() => setBreakdownArea(null)} 
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default PublicGuidePage;
